import React from 'react';
import { getScoringRequest } from '../../services/loanRetrievalService';
import { postDeviceData } from '../../services/scoringService';
import Loader from '../../components/Loader';
import SITE_PATHS from '../../config/sitePaths';
import appSyncClient from '../../appsync/client';
import createUser from '../../appsync/mutations/CreateUser';
import Text from '../../components/Text';
import LoanDetailsView from './LoanDetails.view';
import { LanguageContext } from '../../config/LanguageContext';

import './style.scss';

const errorInScoringRequest = (scoringRequest) => (
  !scoringRequest?.status || !(scoringRequest.dateOriginated || scoringRequest.dateApplied)
);

class LoanDetails extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      error: false,
      loading: true,
      scoringRequest: {},
    };
    this.confirmLoan = this.confirmLoan.bind(this);
    this.goBack = this.goBack.bind(this);
  }

  async componentDidMount() {
    const {
      onUpdateLanguage,
      location: {
        state: locationState,
      } = {},
    } = this.props;
    if (!locationState) return;
    try {
      const promises = [];
      if (locationState.scoringRequest && !locationState.isEmbedded) {
        this.setState({ scoringRequest: locationState.scoringRequest });
        promises.push(this.createGuestUser(locationState.scoringRequest));
        if (locationState.autoConfirmLoan) promises.push(this.confirmLoan(locationState.scoringRequest));
      } else {
        promises.push(this.loadScoringRequest());
      }
      if (locationState.language) {
        onUpdateLanguage(locationState.language);
      }
      await Promise.all(promises);
    } catch (error) {
      console.error('Error loading loan details:', error); // eslint-disable-line no-console
      this.setState({ error });
    } finally {
      this.setState({ loading: false });
    }
  }

  createGuestUser = async (scoringRequest) => {
    const {
      guestUserInfo,
      guestUserAgreement,
    } = this.props;
    if (!guestUserInfo || this.state.isRedirectedFromStandalone || !guestUserAgreement) {
      return false;
    }
    const {
      guestUserAgreement: {
        agreement: {
          allowResearch = false,
        } = {},
      } = {},
    } = this.props;
    const { primaryEmail } = scoringRequest;
    const timestamp = new Date().toISOString();

    return appSyncClient
      .mutate({
        mutation: createUser(
          primaryEmail,
          allowResearch,
          timestamp,
          true,
          false,
          this.context.language,
        ),
      });
  };

  loadScoringRequest = async () => {
    const {
      location: {
        state: {
          clientId,
          requestId,
          autoConfirmLoan,
        },
      },
    } = this.props;
    const scoringRequest = await getScoringRequest(clientId, requestId);
    if (scoringRequest.errorType !== undefined) {
      this.setState({ error: scoringRequest });
      return;
    }
    this.setState({ scoringRequest });
    await Promise.all([
      this.createGuestUser(scoringRequest),
      autoConfirmLoan && this.confirmLoan(scoringRequest),
    ]);
  };

  confirmLoan(scoringRequest) {
    const {
      location: {
        state: {
          skipBankingSurvey,
          connectURL,
          webhookData,
          bankingConfig: bankingConfigFromProps,
        },
      },
    } = this.props;
    const {
      clientId,
      requestId,
      trustStartPublicConfig: {
        banking: bankingConfigFromScoringRequest,
      } = {},
    } = scoringRequest;

    const bankingConfig = bankingConfigFromProps ?? bankingConfigFromScoringRequest;

    return postDeviceData({
      type: 'DeviceInfo',
      dataBody: {
        ApplicationName: 'TrustStartWeb',
        UserAgent: window.navigator.userAgent,
      },
      miningId: requestId,
      clientId,
    })
      .then(() => {
        if (bankingConfig && bankingConfig.enabled) {
          this.props.onNextStep(
            this.props.history,
            {
              scoringRequest,
              bankingConfig,
              skipBankingSurvey,
              connectURL,
              webhookData,
            },
          );
          return;
        }

        this.props.onNextStep(this.props.history, { scoringRequest });
      })
      .catch(console.error); // eslint-disable-line no-console
  }

  goBack() {
    this.props.history.push(
      SITE_PATHS.LOANS_LISTING,
      { scoringRequest: this.state.scoringRequest },
    );
  }

  renderError() {
    return (
      <div className="errorMessageWrapper">
        <p>
          <Text value="Unable to load loan details. Please try again later." />
        </p>
        <button type="button" className="btn btn-secondary" onClick={this.goBack}>
          <Text value="Go Back" />
        </button>
      </div>
    );
  }

  render() {
    const {
      location: {
        state: {
          autoConfirmLoan,
        } = {},
      } = {},
    } = this.props;
    const {
      error,
      loading,
      scoringRequest,
    } = this.state;

    if (error) return this.renderError();
    if (loading || autoConfirmLoan) return <Loader />;
    if (errorInScoringRequest(scoringRequest)) return this.renderError();

    return (
      <LoanDetailsView
        scoringRequest={scoringRequest}
        companyDisplayName={scoringRequest.companyDisplayName}
        dateApplied={scoringRequest.dateOriginated || scoringRequest.dateApplied}
        status={scoringRequest.status}
        balance={scoringRequest.balance}
        primaryEmail={scoringRequest.primaryEmail}
        goBack={this.goBack}
        confirmLoan={this.confirmLoan}
      />
    );
  }
}

LoanDetails.contextType = LanguageContext;

export default LoanDetails;
